import * as types from '../mutation-types';
import { Module } from 'vuex';
import api from '../../api/modules/community';
import _ from 'lodash';

const findComment = (comments, commentId) => {
  for (let i = 0; i < comments.length; i++) {
    if (comments[i].id === commentId) {
      return {
        commentsArray: comments,
        comment: comments[i],
      };
    }
    const found = findComment(comments[i].replies, commentId);
    if (found) return found;
  }
  return null;
};

interface State {
  numberOfNotifications: number;
  numberOfStudioNotifications: number;
  numberOfChatNotifications: number;
  numberOfStudioChatNotifications: number;
  notificationsToken: string | null;
  studioNotificationsToken: string | null;
  notifications: CommunityNotificationsResponse;
  studioNotifications: CommunityNotificationsResponse;
  notificationsLimit: number;
  notificationsOffset: number;
  studioNotificationsOffset: number;
  canLoadMoreNotifications: boolean;
  canLoadMoreStudioNotifications: boolean;
  canLoadMoreGiphies: boolean;
  interactAsStudio: boolean;
  teacherGroups: CommunityGroupsResponse[] | null;
  studioGroups: CommunityGroupsResponse[] | null;
  currentGroup: CommunityGroupsResponse | null;
  comments: CommunityCommentResponse[] | null;
  studioMembers: StudioCommunityMembersResponse[] | null;
  chatClient: object | null;
}

const initialState: State = {
  numberOfNotifications: 0,
  numberOfStudioNotifications: 0,
  numberOfChatNotifications: 0,
  numberOfStudioChatNotifications: 0,
  notificationsToken: null,
  studioNotificationsToken: null,
  notifications: {} as CommunityNotificationsResponse,
  studioNotifications: {} as CommunityNotificationsResponse,
  notificationsLimit: 10,
  notificationsOffset: 0,
  studioNotificationsOffset: 0,
  canLoadMoreNotifications: false,
  canLoadMoreStudioNotifications: false,
  canLoadMoreGiphies: false,
  interactAsStudio: false,
  teacherGroups: [],
  studioGroups: [],
  currentGroup: null,
  comments: [],
  studioMembers: [],
  chatClient: null,
};

const communityModule: Module<State, any> = {
  namespaced: true,
  state: initialState,
  getters: {
    numberOfNotifications: state => state.numberOfNotifications,
    numberOfStudioNotifications: state => state.numberOfStudioNotifications,
    numberOfChatNotifications: state => state.numberOfChatNotifications,
    numberOfStudioChatNotifications: state =>
      state.numberOfStudioChatNotifications,
    notifications: state => state.notifications,
    studioNotifications: state => state.studioNotifications,
    canLoadMoreNotifications: state => state.canLoadMoreNotifications,
    canLoadMoreStudioNotifications: state =>
      state.canLoadMoreStudioNotifications,
    canLoadMoreGiphies: state => state.canLoadMoreGiphies,
    interactAsStudio: state => state.interactAsStudio,
    teacherGroups: state => state.teacherGroups,
    studioGroups: state => state.studioGroups,
    currentGroup: state => state.currentGroup,
    comments: state => state.comments,
    studioMembers: state => state.studioMembers,
    chatClient: state => state.chatClient,
  },
  actions: {
    commentAdded({ commit }, comment) {
      commit(types.COMMENT_ADDED, comment);
    },
    disconnectChatClient({ state }) {
      if (state.chatClient) {
        try {
          // @ts-ignore
          state.chatClient.disconnect();
        } catch (error) {
          console.error(error);
        }
      }
    },
    async loadComments(
      { commit, state },
      { entityOwnerId, entityType, entityId, page, commentId }
    ) {
      if (page === 1) {
        commit(types.SET_COMMENTS, []);
      }
      const comments = await api.loadComments(
        entityOwnerId,
        entityType,
        entityId,
        state.interactAsStudio,
        page,
        commentId
      );
      if (page > 1) {
        // @ts-ignore
        commit(types.SET_COMMENTS, [...state.comments, ...comments]);
      } else {
        commit(types.SET_COMMENTS, comments);
      }
      return state.comments;
    },
    async loadScheduledComments(
      { commit, state },
      { entityOwnerId, entityType, entityId }
    ) {
      const comments = await api.loadScheduledComments(
        entityOwnerId,
        entityType,
        entityId,
        state.interactAsStudio
      );
      return comments;
    },
    async searchComments(
      { commit, state },
      { entityOwnerId, entityType, entityId, query, page }
    ) {
      if (page === 1) {
        commit(types.SET_COMMENTS, []);
      }
      const comments = await api.searchComments(
        entityOwnerId,
        entityType,
        entityId,
        state.interactAsStudio,
        query,
        page
      );
      if (page > 1) {
        // @ts-ignore
        commit(types.SET_COMMENTS, [...state.comments, ...comments]);
      } else {
        commit(types.SET_COMMENTS, comments);
      }
      return state.comments;
    },
    async postComment(
      { commit },
      {
        parentId,
        entityOwnerId,
        entityType,
        entityId,
        content,
        text,
        additionalData,
        isStudio,
        attachment,
        hashtag,
        releaseOn,
      }
    ) {
      const comment = await api.postComment(
        parentId,
        entityOwnerId,
        entityType,
        entityId,
        content,
        text,
        additionalData,
        isStudio,
        attachment,
        hashtag,
        releaseOn
      );
      commit(types.COMMENT_ADDED, comment);
      return comment;
    },
    async editComment(
      { commit },
      {
        commentId,
        content,
        text,
        additionalData,
        isStudio,
        attachment,
        releaseOn,
        hashtag,
      }
    ) {
      const comment = await api.editComment(
        commentId,
        content,
        text,
        additionalData,
        isStudio,
        attachment,
        releaseOn,
        hashtag
      );
      commit(types.COMMENT_EDITED, comment);
      return comment;
    },
    likeComment({ state }, { commentId }) {
      return api.likeComment(commentId, state.interactAsStudio);
    },
    unlikeComment({ state }, { commentId }) {
      return api.unlikeComment(commentId, state.interactAsStudio);
    },
    hideComment({ state }, { commentId }) {
      return api.hideComment(commentId, state.interactAsStudio);
    },
    showComment({ state }, { commentId }) {
      return api.showComment(commentId, state.interactAsStudio);
    },
    async deleteComment({ commit, state }, { commentId }) {
      const comment = await api.deleteComment(
        commentId,
        state.interactAsStudio
      );
      commit(types.COMMENT_DELETED, comment);
      return comment;
    },
    undeleteComment({ state }, { commentId }) {
      return api.undeleteComment(commentId, state.interactAsStudio);
    },
    deleteFlaggedComment({ state }, { commentId, isStudio }) {
      return api.deleteComment(commentId, isStudio);
    },
    loadUserData(_, { studioURL, uuid }) {
      return api.loadUserData(studioURL, uuid);
    },
    loadMyCommunityProfileData({ state }) {
      return api.loadCommunityProfileData(state.interactAsStudio);
    },
    loadStudentCommunityProfileData(_) {
      return api.loadCommunityProfileData(false);
    },
    saveMyCommunityProfileData(_, data) {
      return api.saveCommunityProfileData(data);
    },
    async getNotificationsToken({ commit }) {
      const tokenResponse: CommunityTokenResponse = await api.getNotificationsToken(
        false
      );
      commit(types.SET_NOTIFICATIONS_TOKEN, tokenResponse.token);
      return tokenResponse;
    },
    async getStudioNotificationsToken({ commit }) {
      const tokenResponse: CommunityTokenResponse = await api.getNotificationsToken(
        true
      );
      commit(types.SET_STUDIO_NOTIFICATIONS_TOKEN, tokenResponse.token);
      return tokenResponse;
    },
    searchUsers(_, { studioURL, query }) {
      return api.searchUsers(studioURL, query);
    },
    async markNotificationSeen(
      { commit, state },
      { notificationId, isStudio }
    ) {
      await api.markNotificationSeen(notificationId, isStudio);
      if (isStudio) {
        commit(
          types.SET_NUMBER_OF_STUDIO_NOTIFICATIONS,
          state.numberOfStudioNotifications - 1
        );
      } else {
        commit(
          types.SET_NUMBER_OF_NOTIFICATIONS,
          state.numberOfNotifications - 1
        );
      }
    },
    async loadNotifications({ commit, state }, { markSeen }) {
      commit(types.SET_NOTIFICATIONS_OFFSET, 0);
      commit(types.SET_NOTIFICATIONS, {});
      const notifications = await api.loadNotifications(
        markSeen,
        state.notificationsOffset,
        state.notificationsLimit,
        false
      );
      commit(types.SET_NOTIFICATIONS, notifications);
      commit(types.SET_NUMBER_OF_NOTIFICATIONS, notifications.unseen);
      commit(
        types.SET_CAN_LOAD_MORE_NOTIFICATIONS,
        notifications.results.length > 0
      );
      return state.notifications;
    },
    async loadNextNotifications({ commit, state }, { markSeen }) {
      commit(
        types.SET_NOTIFICATIONS_OFFSET,
        state.notificationsOffset + state.notificationsLimit
      );
      const notifications = await api.loadNotifications(
        markSeen,
        state.notificationsOffset,
        state.notificationsLimit,
        false
      );
      commit(types.SET_NEXT_NOTIFICATIONS, notifications);
      commit(types.SET_NUMBER_OF_NOTIFICATIONS, notifications.unseen);
      commit(
        types.SET_CAN_LOAD_MORE_NOTIFICATIONS,
        notifications.results.length > 0
      );
      return state.notifications;
    },
    async loadStudioNotifications({ commit, state }, { markSeen }) {
      commit(types.SET_STUDIO_NOTIFICATIONS_OFFSET, 0);
      commit(types.SET_STUDIO_NOTIFICATIONS, {});
      const notifications = await api.loadNotifications(
        markSeen,
        state.studioNotificationsOffset,
        state.notificationsLimit,
        true
      );
      commit(types.SET_STUDIO_NOTIFICATIONS, notifications);
      commit(types.SET_NUMBER_OF_STUDIO_NOTIFICATIONS, notifications.unseen);
      commit(
        types.SET_CAN_LOAD_MORE_STUDIO_NOTIFICATIONS,
        notifications.results.length > 0
      );
      return state.studioNotifications;
    },
    setNumberOfChatNotifications({ commit, state }, numberOfNotifications) {
      commit(types.SET_NUMBER_OF_CHAT_NOTIFICATIONS, numberOfNotifications);
    },
    setNumberOfStudioChatNotifications(
      { commit, state },
      numberOfNotifications
    ) {
      commit(
        types.SET_NUMBER_OF_STUDIO_CHAT_NOTIFICATIONS,
        numberOfNotifications
      );
    },
    async loadNextStudioNotifications({ commit, state }, { markSeen }) {
      commit(
        types.SET_STUDIO_NOTIFICATIONS_OFFSET,
        state.studioNotificationsOffset + state.notificationsLimit
      );
      const notifications = await api.loadNotifications(
        markSeen,
        state.studioNotificationsOffset,
        state.notificationsLimit,
        true
      );
      commit(types.SET_NEXT_STUDIO_NOTIFICATIONS, notifications);
      commit(types.SET_NUMBER_OF_STUDIO_NOTIFICATIONS, notifications.unseen);
      commit(
        types.SET_CAN_LOAD_MORE_STUDIO_NOTIFICATIONS,
        notifications.results.length > 0
      );
      return state.notifications;
    },
    processNotificationsCallback({ commit, state }, notificationsData) {
      const newNotifications = notificationsData.new?.length;
      const deletedNotifications = notificationsData.deleted?.length;
      if (newNotifications > 0) {
        commit(
          types.SET_NUMBER_OF_NOTIFICATIONS,
          state.numberOfNotifications + newNotifications
        );
      }
      if (deletedNotifications > 0) {
        commit(
          types.SET_NUMBER_OF_NOTIFICATIONS,
          state.numberOfNotifications - deletedNotifications
        );
      }
    },
    processStudioNotificationsCallback({ commit, state }, notificationsData) {
      const newNotifications = notificationsData.new?.length;
      const deletedNotifications = notificationsData.deleted?.length;
      if (newNotifications > 0) {
        commit(
          types.SET_NUMBER_OF_STUDIO_NOTIFICATIONS,
          state.numberOfStudioNotifications + newNotifications
        );
      }
      if (deletedNotifications > 0) {
        commit(
          types.SET_NUMBER_OF_STUDIO_NOTIFICATIONS,
          state.numberOfStudioNotifications - deletedNotifications
        );
      }
    },
    async loadGiphy(
      { commit, state },
      { searchURL, apiKey, searchWord, searchLimit, offset }
    ) {
      const giphies = (await api.loadGiphy(
        searchURL,
        apiKey,
        searchWord,
        searchLimit,
        offset
      )) as any;
      commit(types.SET_CAN_LOAD_MORE_GIPHY, giphies?.data?.length > 0);
      return giphies;
    },
    loadGroup(_, { id }) {
      return api.loadGroup(id);
    },
    async loadGroups({ commit, state }) {
      commit(types.SET_TEACHER_GROUPS, []);
      const groups = await api.loadGroups();
      commit(types.SET_TEACHER_GROUPS, groups);
      return state.teacherGroups;
    },
    addGroup(
      _,
      {
        group_name,
        cover_photo,
        thumbnail_photo,
        products,
        hashtags_enabled,
        hashtags,
        display_members,
      }
    ) {
      return api.addGroup(
        group_name,
        cover_photo,
        thumbnail_photo,
        products,
        hashtags_enabled,
        hashtags,
        display_members
      );
    },
    editGroup(
      _,
      {
        id,
        group_name,
        cover_photo,
        thumbnail_photo,
        products,
        hashtags_enabled,
        hashtags,
        display_members,
        is_free,
      }
    ) {
      return api.editGroup(
        id,
        group_name,
        cover_photo,
        thumbnail_photo,
        products,
        hashtags_enabled,
        hashtags,
        display_members,
        is_free
      );
    },
    editGroupDescription(_, { id, about_text }) {
      return api.editGroupDescription(id, about_text);
    },
    archiveGroup(_, { id, active }) {
      return api.archiveGroup(id, active);
    },
    loadMembers(_, { groupId, q, page }) {
      return api.loadMembers(groupId, q, page);
    },
    getMember(_, { groupId, memberId }) {
      return api.getMember(groupId, memberId);
    },
    banMember(_, { groupId, memberId }) {
      return api.banMember(groupId, memberId);
    },
    unBanMember(_, { groupId, memberId }) {
      return api.unBanMember(groupId, memberId);
    },
    flagComment(_, { commentId }) {
      return api.flagComment(commentId);
    },
    unFlagComment(_, { commentId }) {
      return api.unFlagComment(commentId);
    },
    pinComment(_, { commentId }) {
      return api.pinComment(commentId);
    },
    unPinComment(_, { commentId }) {
      return api.unPinComment(commentId);
    },
    loadFlaggedComments(_, { page, q, ordering }) {
      return api.loadFlaggedComments(page, q, ordering);
    },
    setInteractAsStudio({ commit }, asStudio: boolean) {
      commit(types.SET_INTERACT_AS_STUDIO, asStudio);
    },
    async loadStudioGroups({ commit, state }, { studioURL }) {
      commit(types.SET_STUDIO_GROUPS, []);
      const groups = await api.loadStudioGroups(studioURL);
      commit(types.SET_STUDIO_GROUPS, groups);
      return state.studioGroups;
    },
    async loadStudioGroup({ commit }, { studioURL, groupId }) {
      const group = await api.loadStudioGroup(studioURL, groupId);
      commit(types.SET_STUDIO_GROUP, group);
      return group;
    },
    async loadCommunityGroupMembers(
      { commit, state },
      { studioURL, groupId, q, page, cancelToken }
    ) {
      commit(types.SET_STUDIO_MEMBERS, []);
      const members = await api.loadCommunityGroupMembers(
        studioURL,
        groupId,
        q,
        page,
        cancelToken
      );
      commit(types.SET_STUDIO_MEMBERS, members);
      return state.studioMembers;
    },
    loadStudioMember(_, { studioURL, groupId, memberId }) {
      return api.loadStudioMember(studioURL, groupId, memberId);
    },
    createPublicChatRoom(_, { studioURL, payload }) {
      return api.createPublicChatRoom(studioURL, payload);
    },
    editPublicChatRoom(_, { studioURL, roomId, payload }) {
      return api.editPublicChatRoom(studioURL, roomId, payload);
    },
    addMemberToChatRoom(_, { studioURL, member, roomId }) {
      return api.addMemberToChatRoom(studioURL, member, roomId);
    },
    createPrivateChatRoom(_, { studioURL, member }) {
      return api.createPrivateChatRoom(studioURL, member);
    },
    getPrivateChatRoom(_, { studioURL, roomId }) {
      return api.getPrivateChatRoom(studioURL, roomId);
    },
    getPublicChatRoom(_, { studioURL, roomId }) {
      return api.getPublicChatRoom(studioURL, roomId);
    },
    loadChatRoomMembers(_, { studioURL, roomId }) {
      return api.loadChatRoomMembers(studioURL, roomId);
    },
    removeMemberFromChatRoom(_, { studioURL, member, roomId }) {
      return api.removeMemberFromChatRoom(studioURL, member, roomId);
    },
    async loadChatRooms(_, { studioURL, query, id, page }) {
      return api.loadChatRooms(studioURL, query, id, page);
    },
    communityChatClient({ commit, state }, { chatClient }) {
      commit(types.SET_CHAT_CLIENT, chatClient);
      return state.chatClient;
    },
    async getCommentLikes(_, params): Promise<PaginatedResponse<any>> {
      const data = await api.getCommentLikes(params.page, params.id);
      return data;
    },
    rejoinGroup(_, { groupId, studioURL }) {
      return api.rejoinGroup(groupId, studioURL);
    },
    leaveGroup(_, { groupId, studioURL }) {
      return api.leaveGroup(groupId, studioURL);
    },
  },
  mutations: {
    [types.SET_NOTIFICATIONS](
      state: State,
      notifications: CommunityNotificationsResponse
    ) {
      state.notifications = notifications;
    },
    [types.SET_NEXT_NOTIFICATIONS](
      state: State,
      notifications: CommunityNotificationsResponse
    ) {
      state.notifications = {
        ...state.notifications,
        results: [
          ...(state.notifications?.results || []),
          ...(notifications?.results || []),
        ],
      };
    },
    [types.SET_STUDIO_NOTIFICATIONS](
      state: State,
      notifications: CommunityNotificationsResponse
    ) {
      state.studioNotifications = notifications;
    },
    [types.SET_NEXT_STUDIO_NOTIFICATIONS](
      state: State,
      notifications: CommunityNotificationsResponse
    ) {
      state.studioNotifications = {
        ...state.studioNotifications,
        results: [
          ...(state.studioNotifications?.results || []),
          ...(notifications?.results || []),
        ],
      };
    },
    [types.SET_NOTIFICATIONS_OFFSET](
      state: State,
      notificationsOffset: number
    ) {
      state.notificationsOffset = notificationsOffset;
    },
    [types.SET_STUDIO_NOTIFICATIONS_OFFSET](
      state: State,
      notificationsOffset: number
    ) {
      state.studioNotificationsOffset = notificationsOffset;
    },
    [types.SET_CAN_LOAD_MORE_NOTIFICATIONS](
      state: State,
      canLoadMoreNotifications: boolean
    ) {
      state.canLoadMoreNotifications = canLoadMoreNotifications;
    },
    [types.SET_CAN_LOAD_MORE_STUDIO_NOTIFICATIONS](
      state: State,
      canLoadMoreNotifications: boolean
    ) {
      state.canLoadMoreStudioNotifications = canLoadMoreNotifications;
    },
    [types.SET_NUMBER_OF_NOTIFICATIONS](
      state: State,
      numberOfNotifications: number
    ) {
      state.numberOfNotifications = Math.max(numberOfNotifications, 0);
    },
    [types.SET_NUMBER_OF_STUDIO_NOTIFICATIONS](
      state: State,
      numberOfNotifications: number
    ) {
      state.numberOfStudioNotifications = Math.max(numberOfNotifications, 0);
    },
    [types.SET_NUMBER_OF_CHAT_NOTIFICATIONS](
      state: State,
      numberOfNotifications: number
    ) {
      state.numberOfChatNotifications = Math.max(numberOfNotifications, 0);
    },
    [types.SET_NUMBER_OF_STUDIO_CHAT_NOTIFICATIONS](
      state: State,
      numberOfNotifications: number
    ) {
      state.numberOfStudioChatNotifications = Math.max(
        numberOfNotifications,
        0
      );
    },
    [types.SET_NOTIFICATIONS_TOKEN](state: State, token: string) {
      state.notificationsToken = token;
    },
    [types.SET_STUDIO_NOTIFICATIONS_TOKEN](state: State, token: string) {
      state.studioNotificationsToken = token;
    },
    [types.SET_CAN_LOAD_MORE_GIPHY](state: State, canLoadMoreGiphies: boolean) {
      state.canLoadMoreGiphies = canLoadMoreGiphies;
    },
    [types.SET_INTERACT_AS_STUDIO](state: State, interactAsStudio: boolean) {
      state.interactAsStudio = interactAsStudio;
    },
    [types.SET_TEACHER_GROUPS](
      state: State,
      groups: CommunityGroupsResponse[]
    ) {
      state.teacherGroups = groups;
    },
    [types.SET_STUDIO_GROUPS](state: State, groups: CommunityGroupsResponse[]) {
      state.studioGroups = groups;
    },
    [types.SET_STUDIO_GROUP](state: State, group: CommunityGroupsResponse) {
      state.currentGroup = group;
    },
    [types.SET_COMMENTS](state: State, comments: CommunityCommentResponse[]) {
      state.comments = comments;
    },
    [types.SET_STUDIO_MEMBERS](
      state: State,
      members: StudioCommunityMembersResponse[]
    ) {
      state.studioMembers = members;
    },
    [types.SET_CHAT_CLIENT](state: State, chatClient: object) {
      state.chatClient = chatClient;
    },
    [types.COMMENT_ADDED](state: State, comment) {
      if (!comment.parent) {
        state.comments = [comment, ...state.comments];
      } else {
        const foundParentObj = findComment(state.comments, comment.parent);
        if (!foundParentObj) return;
        if (!foundParentObj.comment) return;
        foundParentObj.comment.replies = [
          comment,
          ...foundParentObj.comment.replies,
        ];
        state.comments = _.cloneDeep(state.comments);
      }
    },
    [types.COMMENT_EDITED](state: State, comment) {
      const foundObj = findComment(state.comments, comment.id);
      if (!foundObj) return;
      if (!foundObj.comment || !foundObj.commentsArray) return;

      const index = foundObj.commentsArray.indexOf(foundObj.comment);
      foundObj.commentsArray.splice(index, 1, { ...comment });
      state.comments = _.cloneDeep(state.comments);
    },
    [types.COMMENT_DELETED](state: State, comment) {
      const foundObj = findComment(state.comments, comment.id);
      if (!foundObj) return;
      if (!foundObj.comment || !foundObj.commentsArray) return;

      const index = foundObj.commentsArray.indexOf(foundObj.comment);
      foundObj.commentsArray.splice(index, 1);
      state.comments = _.cloneDeep(state.comments);
    },
  },
};
export default communityModule;
